import { useEffect, useMemo } from "react";
import { useUser } from "context";
import { useCheckPermission } from "hooks";
import { useSubscriptionInfo } from "api/useSubscriptionInfo";
import { useProfileTemplatesList } from "features/profile-templates/api/useProfileTemplatesList";
import { type ITeamUserObject } from "types/TeamUsersResponse";
import { FEATURE_ACTION, FEATURES } from "constants/features";
import { INVITE_USER_MODAL_TEXTS } from "constants/Texts";
import { type IMsg } from "../MessagesContainer";
import { useMultiEmail } from "../email/useMultiEmail";
import { type TCTAButtonProps } from "../CTAButton";
import { type IEmailWithTeam } from "../email/useValidateEmails";
import {
  clearPendingEmails,
  loadPendingEmails,
} from "../utils/pendingEmailsStorage";

export function useInviteUser(
  teamUsers: ITeamUserObject[] | undefined,
  isLoading: boolean,
  isOpen: boolean,
) {
  const [{ currentTeam }] = useUser();
  const { data: subscriptionInfo, ...subscriptionInfoRest } =
    useSubscriptionInfo({ enabled: isOpen });
  const canEditOrderMoreCards = useCheckPermission(
    FEATURE_ACTION.EDIT,
    FEATURES.ORDER_MORE_CARDS,
  );
  const canEditManageSubscription = useCheckPermission(
    FEATURE_ACTION.EDIT,
    FEATURES.MANAGE_SUBSCRIPTION,
  );
  const isSubscriptionInfoLoading =
    subscriptionInfoRest.isLoading || !subscriptionInfoRest.isSuccess;
  const hasScheduledSeatChange =
    !!subscriptionInfo?.current_subscription?.reduction_schedule;

  const currentTeamInfo = useMemo(() => {
    const {
      num_seats: numSeats = 0,
      assigned_seats: assignedSeats = 0,
      pending_seats: pendingSeats = 0,
      id: currentTeamId = 0,
    } = currentTeam || {};

    const assignedAndPendingSeats = assignedSeats + pendingSeats;
    const availableSeats = numSeats - assignedSeats - pendingSeats;

    return {
      availableSeats,
      assignedAndPendingSeats,
      totalSeats: numSeats,
      currentTeamId,
    };
  }, [currentTeam]);

  const userList = useMemo(
    () => teamUsers?.map(({ email }) => email) || [],
    [teamUsers],
  );

  const multiEmailInput = useMultiEmail({
    currentTeamId: currentTeamInfo.currentTeamId,
    limit: currentTeamInfo.availableSeats,
    userList,
  });

  const { data: templatesData, isLoading: templatesQueryIsLoading } =
    useProfileTemplatesList({
      teamId: currentTeamInfo.currentTeamId,
    });
  const templates = templatesData?.templates;
  const defaultTemplateId = useMemo(
    () => templates?.find(t => t.is_default)?.id ?? null,
    [templates],
  );
  const isTemplatesLoading = templatesQueryIsLoading;

  const {
    isLimitExceeded,
    exceededMailCount,
    emailsList,
    emailsGroupedBystatus,
    emailsWithNoTeamId,
    emailValidationRest,
    setEmailsMap,
    validateEmails,
  } = multiEmailInput;
  const { currentTeamId, assignedAndPendingSeats } = currentTeamInfo;
  const reductionSeatCount =
    subscriptionInfo?.current_subscription?.reduction_schedule?.new_seat_count;
  const targetTotalSeatCount =
    assignedAndPendingSeats + emailsWithNoTeamId.length;

  const emailsGroupedByTeam = useMemo(() => {
    return emailsList.reduce(
      (grouped, email) => {
        if (email.teamId === currentTeamId) {
          grouped.currentTeam.push(email);
        } else if (email.teamId) {
          grouped.anotherTeams.push(email);
        } else {
          grouped.withoutTeam.push(email);
        }
        return grouped;
      },
      {
        currentTeam: [] as IEmailWithTeam[],
        anotherTeams: [] as IEmailWithTeam[],
        withoutTeam: [] as IEmailWithTeam[],
      },
    );
  }, [emailsList, currentTeamId]);

  const getTeamConflictMessages = (): IMsg[] => {
    const messages: IMsg[] = [];

    if (emailsGroupedByTeam.currentTeam.length) {
      messages.push({
        type: "error",
        iconName: "confirmed",
        text: INVITE_USER_MODAL_TEXTS[
          emailsGroupedByTeam.currentTeam.length > 1
            ? "USERS_ALREADY_IN_THIS_TEAM"
            : "USER_ALREADY_IN_THIS_TEAM"
        ],
      });
    }

    if (emailsGroupedByTeam.anotherTeams.length) {
      messages.push({
        type: "error",
        iconName: "groups",
        text: INVITE_USER_MODAL_TEXTS[
          emailsGroupedByTeam.anotherTeams.length > 1
            ? "USERS_ALREADY_IN_ANOTHER_TEAM"
            : "USER_ALREADY_IN_ANOTHER_TEAM"
        ],
      });
    }

    return messages;
  };

  const getLimitExceededMessages = (): IMsg[] => {
    const messages: IMsg[] = [];

    if (isLimitExceeded) {
      if (canEditManageSubscription || canEditOrderMoreCards) {
        if (exceededMailCount) {
          const preFix = hasScheduledSeatChange
            ? INVITE_USER_MODAL_TEXTS.SCHEDULED_SEAT_CHANGE_CANCELLED + "\r\n"
            : "";
          const additionalSeats =
            emailsWithNoTeamId.length - currentTeamInfo.availableSeats;
          const text = canEditManageSubscription
            ? INVITE_USER_MODAL_TEXTS[
                additionalSeats > 1 ? "ADDITIONAL_SEATS" : "ADDITIONAL_SEAT"
              ]
            : INVITE_USER_MODAL_TEXTS[
                additionalSeats > 1 ? "ADDITIONAL_CARDS" : "ADDITIONAL_CARD"
              ];
          messages.push({
            type: "info",
            iconName: "info",
            text: preFix + text.replace(/{count}/g, additionalSeats.toString()),
          });
        }
      } else {
        messages.push({
          type: "error",
          iconName: "increaseUsers",
          text: INVITE_USER_MODAL_TEXTS.MAX_SEATS,
        });
      }
    }

    return messages;
  };

  const getScheduledSeatChangeMessages = (): IMsg[] => {
    const messages: IMsg[] = [];

    // Only show scheduled seat change message if we're going to exceed the limit
    if (
      !isLimitExceeded &&
      reductionSeatCount &&
      targetTotalSeatCount > reductionSeatCount
    ) {
      messages.push({
        type: "info",
        iconName: "info",
        text: INVITE_USER_MODAL_TEXTS.INVITE_USER_CANCEL_SEAT_CHANGE,
      });
    }

    return messages;
  };

  const messages = useMemo(() => {
    return [
      ...getTeamConflictMessages(),
      ...getLimitExceededMessages(),
      ...getScheduledSeatChangeMessages(),
    ];
  }, [
    canEditManageSubscription,
    canEditOrderMoreCards,
    currentTeamInfo.availableSeats,
    isLimitExceeded,
    exceededMailCount,
    emailsGroupedByTeam,
    hasScheduledSeatChange,
    emailsWithNoTeamId.length,
    targetTotalSeatCount,
  ]);

  const buttonRestProps = useMemo<TCTAButtonProps>(() => {
    const commonProps: Pick<TCTAButtonProps, "isLoading" | "isDisabled"> = {
      isDisabled: !emailsWithNoTeamId.length, // NOTE: Will be overridden by the case
      isLoading:
        isLoading ||
        isSubscriptionInfoLoading ||
        isTemplatesLoading ||
        emailValidationRest.isLoading ||
        !!emailsGroupedBystatus.error.length ||
        !!emailsGroupedBystatus.pending.length,
    };

    if (!isLimitExceeded) {
      return {
        ...commonProps,
        type: "invite",
      };
    }

    if (canEditOrderMoreCards) {
      return {
        ...commonProps,
        type: "add-more-cards",
        teamId: currentTeamId,
      };
    }

    if (canEditManageSubscription) {
      return {
        ...commonProps,
        type: "increase-seats",
      };
    }

    return {
      ...commonProps,
      type: "invite",
      isDisabled: true,
    };
  }, [
    canEditOrderMoreCards,
    canEditManageSubscription,
    currentTeamId,
    emailsWithNoTeamId.length,
    isLimitExceeded,
    isLoading,
    emailValidationRest.isLoading,
    isSubscriptionInfoLoading,
    isTemplatesLoading,
    emailsGroupedBystatus.error.length,
    emailsGroupedBystatus.pending.length,
  ]);

  useEffect(() => {
    if (isOpen) {
      const pendingEmails = loadPendingEmails();
      if (pendingEmails.length > 0) {
        const initialEmailsMap = new Map();
        pendingEmails.forEach(email => {
          initialEmailsMap.set(email, {
            email,
            teamId: null,
            status: "pending" as const,
          });
        });
        setEmailsMap(initialEmailsMap);
        validateEmails(Array.from(initialEmailsMap.values()));
        clearPendingEmails(); // Ensture restoring emails just once!
      }
    } else {
      setEmailsMap(new Map());
    }
  }, [isOpen]);

  return {
    ...currentTeamInfo,
    ...multiEmailInput,
    messages,
    buttonRestProps,
    emailsGroupedBystatus,
    exceededMailCount,
    isSubscriptionInfoLoading,
    hasScheduledSeatChange,
    reductionSeatCount,
    targetTotalSeatCount,
    templates,
    defaultTemplateId,
    isTemplatesLoading,
  };
}
